import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation, useParams } from "react-router-dom";
import AdminLayout from "../components/admin/AdminLayout";
import RequireAdmin from "../components/admin/RequireAdmin";
import RequireCustomer from "../components/auth/RequireCustomer";
import NotFound from "../pages/NotFound";
import AdminDashboard from "../pages/admin/dashboard/page";
import AdminEventDetail from "../pages/admin/event-detail/page";
import AdminEvents from "../pages/admin/events/page";
import AdminCatalog from "../pages/admin/catalog/page";
import AdminInviteAccept from "../pages/admin/invite/page";
import AdminExhibitorApplications from "../pages/admin/exhibitor-applications/page";
import AdminPrintOrder from "../pages/admin/print-order/page";
import AdminTeam from "../pages/admin/team/page";
import AdminUsers from "../pages/admin/users/page";
import AdminLogin from "../pages/admin/login/page";
import AdminOrders from "../pages/admin/orders/page";
import AdminScanner from "../pages/admin/scanner/page";
import AdminSiteContent from "../pages/admin/site-content/page";
import AANWebsite from "../pages/aan-site/page";
import AANContentPage from "../pages/aan-site/ContentPage";
import AANContact from "../pages/aan-site/ContactPage";
import AANDonate from "../pages/aan-site/DonatePage";
import Checkout from "../pages/checkout/page";
import EventDetail from "../pages/event-detail/page";
import Events from "../pages/events/page";
import ForOrganizers from "../pages/for-organizers/page";
import NewsDetail from "../pages/news-detail/page";
import CustomerLogin from "../pages/login/page";
import OrderConfirmation from "../pages/order-confirmation/page";
import OrganizerSignup from "../pages/organizer-signup/page";
import OrganizerStatus from "../pages/organizer-status/page";
import TicketLookup from "../pages/ticket-lookup/page";

function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

function LegacyParamRedirect({ to }: { to: (params: Record<string, string | undefined>) => string }) {
  const location = useLocation();
  const params = useParams();
  return <Navigate to={`${to(params)}${location.search}`} replace />;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <AANWebsite />,
  },
  {
    path: "/news/:slug",
    element: <NewsDetail />,
  },
  {
    path: "/about",
    element: <AANContentPage path="/about" />,
  },
  {
    path: "/about/team",
    element: <AANContentPage path="/about/team" />,
  },
  {
    path: "/projects",
    element: <AANContentPage path="/projects" />,
  },
  {
    path: "/projects/aan-productions",
    element: <AANContentPage path="/projects/aan-productions" />,
  },
  {
    path: "/projects/aan-educ8",
    element: <AANContentPage path="/projects/aan-educ8" />,
  },
  {
    path: "/projects/talent-africa",
    element: <AANContentPage path="/projects/talent-africa" />,
  },
  {
    path: "/projects/adinkra-experience",
    element: <AANContentPage path="/projects/adinkra-experience" />,
  },
  {
    path: "/projects/sankofa-episode",
    element: <AANContentPage path="/projects/sankofa-episode" />,
  },
  {
    path: "/projects/aecinemax",
    element: <AANContentPage path="/projects/aecinemax" />,
  },
  {
    path: "/projects/aad-fund",
    element: <AANContentPage path="/projects/aad-fund" />,
  },
  {
    path: "/join",
    element: <AANContentPage path="/join" />,
  },
  {
    path: "/join/partners",
    element: <AANContentPage path="/join/partners" />,
  },
  {
    path: "/join/volunteers",
    element: <AANContentPage path="/join/volunteers" />,
  },
  {
    path: "/join/members",
    element: <AANContentPage path="/join/members" />,
  },
  {
    path: "/contact",
    element: <AANContact />,
  },
  {
    path: "/donate",
    element: <AANDonate />,
  },
  {
    path: "/screenings",
    element: <Events period="upcoming" />,
  },
  {
    path: "/events/upcoming",
    element: <Events period="upcoming" />,
  },
  {
    path: "/events/past",
    element: <Events period="past" />,
  },
  {
    path: "/events",
    element: <LegacyRedirect to="/events/upcoming" />,
  },
  {
    path: "/screening/:id",
    element: <EventDetail />,
  },
  {
    path: "/event/:id",
    element: <LegacyParamRedirect to={(params) => `/screening/${params.id ?? ""}`} />,
  },
  {
    path: "/login",
    element: <CustomerLogin />,
  },
  {
    path: "/for-exhibitors",
    element: <ForOrganizers />,
  },
  {
    path: "/for-organizers",
    element: <LegacyRedirect to="/for-exhibitors" />,
  },
  {
    path: "/exhibitor/signup",
    element: <OrganizerSignup />,
  },
  {
    path: "/organizer/signup",
    element: <LegacyRedirect to="/exhibitor/signup" />,
  },
  {
    path: "/exhibitor/status",
    element: <OrganizerStatus />,
  },
  {
    path: "/organizer/status",
    element: <LegacyRedirect to="/exhibitor/status" />,
  },
  {
    path: "/checkout/:id",
    element: (
      <RequireCustomer>
        <Checkout />
      </RequireCustomer>
    ),
  },
  {
    path: "/tickets",
    element: (
      <RequireCustomer>
        <TicketLookup />
      </RequireCustomer>
    ),
  },
  {
    path: "/orders/confirmation/:reference",
    element: (
      <RequireCustomer>
        <OrderConfirmation />
      </RequireCustomer>
    ),
  },
  {
    path: "/admin/login",
    element: <AdminLogin />,
  },
  {
    path: "/admin/invite/:token",
    element: <AdminInviteAccept />,
  },
  {
    path: "/admin/orders/:reference/print",
    element: (
      <RequireAdmin>
        <AdminPrintOrder />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin",
    element: (
      <RequireAdmin>
        <AdminLayout />
      </RequireAdmin>
    ),
    children: [
      {
        index: true,
        element: <AdminDashboard />,
      },
      {
        path: "screenings",
        element: <AdminEvents />,
      },
      {
        path: "events",
        element: <LegacyRedirect to="/admin/screenings" />,
      },
      {
        path: "event-approvals",
        element: <LegacyRedirect to="/admin/screenings" />,
      },
      {
        path: "screenings/:id",
        element: <AdminEventDetail />,
      },
      {
        path: "events/:id",
        element: <LegacyParamRedirect to={(params) => `/admin/screenings/${params.id ?? ""}`} />,
      },
      {
        path: "orders",
        element: <AdminOrders />,
      },
      {
        path: "scanner",
        element: <AdminScanner />,
      },
      {
        path: "users",
        element: <AdminUsers />,
      },
      {
        path: "catalog",
        element: <AdminCatalog />,
      },
      {
        path: "site-content",
        element: <AdminSiteContent />,
      },
      {
        path: "team",
        element: <AdminTeam />,
      },
      {
        path: "exhibitor-applications",
        element: <AdminExhibitorApplications />,
      },
      {
        path: "organizer-applications",
        element: <LegacyRedirect to="/admin/exhibitor-applications" />,
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
